import React, { useState, useEffect, useCallback } from 'react';
import { X, Paperclip, Download, FileText, Image, Video, File, Calendar, Eye, Loader2 } from 'lucide-react';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { Contact } from '../types';
import { formatDate, formatDateTime } from '../utils/dateUtils';

interface Attachment {
  filename: string;
  mimeType: string;
  size: number;
  attachmentId: string;
}

interface EmailAttachment {
  attachment: Attachment;
  threadId: string;
  threadSubject: string;
  messageId: string;
  from: string;
  receivedAt: Date;
  source: 'gmail' | 'outlook';
}

interface AttachmentsModalProps {
  contact: Contact;
  onClose: () => void;
  isOpen?: boolean;
}

export const AttachmentsModal: React.FC<AttachmentsModalProps> = ({ contact, onClose, isOpen = true }) => {
  const { t } = useTranslation();
  const [attachments, setAttachments] = useState<EmailAttachment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<'thread' | 'type' | 'date'>('thread');
  const [previewAttachment, setPreviewAttachment] = useState<EmailAttachment | null>(null);
  const [previewData, setPreviewData] = useState<{ dataUrl: string; contentType: string } | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchAttachments = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8881';

      // Fetch all email threads for this contact
      const response = await fetch(
        `${apiUrl}/api/email-threads?contactId=${contact._id}&limit=1000`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || 'Failed to fetch attachments');
      }

      // Extract all attachments from all threads
      const allAttachments: EmailAttachment[] = [];
      (data.data.threads || []).forEach((thread: any) => {
        (thread.messages || []).forEach((message: any) => {
          if (message.attachments && message.attachments.length > 0) {
            message.attachments.forEach((attachment: Attachment) => {
              allAttachments.push({
                attachment,
                threadId: thread.threadId,
                threadSubject: thread.subject,
                messageId: message.messageId,
                from: message.from,
                receivedAt: new Date(message.receivedAt),
                source: thread.source
              });
            });
          }
        });
      });

      // Sort by date (newest first)
      allAttachments.sort((a, b) => b.receivedAt.getTime() - a.receivedAt.getTime());
      setAttachments(allAttachments);
    } catch (err) {
      console.error('Failed to fetch attachments:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch attachments');
    } finally {
      setIsLoading(false);
    }
  }, [contact._id]);

  useEffect(() => {
    if (isOpen) {
      fetchAttachments();
    }
  }, [isOpen, fetchAttachments]);

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) return Image;
    if (mimeType.startsWith('video/')) return Video;
    if (mimeType.includes('pdf')) return FileText;
    return File;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
  };

  const getFileTypeCategory = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'Images';
    if (mimeType.startsWith('video/')) return 'Videos';
    if (mimeType.includes('pdf')) return 'PDFs';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Documents';
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return 'Spreadsheets';
    return 'Other';
  };

  const downloadAttachment = async (attachment: EmailAttachment) => {
    try {
      setDownloadingId(attachment.attachment.attachmentId);
      const token = localStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8881';

      const params = new URLSearchParams({
        threadId: attachment.threadId,
        messageId: attachment.messageId,
        attachmentId: attachment.attachment.attachmentId,
        source: attachment.source
      });

      const response = await fetch(`${apiUrl}/api/email-threads/attachments/download?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download attachment');
      }

      // Get filename from Content-Disposition header or use attachment filename
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = attachment.attachment.filename;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
          // Decode URI-encoded filename
          try {
            filename = decodeURIComponent(filename);
          } catch (e) {
            // If decoding fails, use as-is
          }
        }
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Failed to download attachment:', err);
      alert(err instanceof Error ? err.message : 'Failed to download attachment');
    } finally {
      setDownloadingId(null);
    }
  };

  const handlePreviewAttachment = async (attachment: EmailAttachment) => {
    try {
      setIsLoadingPreview(true);
      setPreviewAttachment(attachment);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('Authentication token not found. Please log in again.');
      }
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8881';

      const params = new URLSearchParams({
        threadId: attachment.threadId,
        messageId: attachment.messageId,
        attachmentId: attachment.attachment.attachmentId,
        source: attachment.source
      });

      const response = await fetch(`${apiUrl}/api/email-threads/attachments/preview?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        let errorMessage = 'Failed to preview attachment';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.status === 401 
            ? 'Authentication failed. Please log in again.'
            : `Failed to preview attachment: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (data.success) {
        setPreviewData({
          dataUrl: data.data.dataUrl,
          contentType: data.data.contentType
        });
      }
    } catch (err) {
      console.error('Failed to preview attachment:', err);
      alert(err instanceof Error ? err.message : 'Failed to preview attachment');
      setPreviewAttachment(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const canPreview = (mimeType: string): boolean => {
    return (
      mimeType.startsWith('image/') ||
      mimeType === 'application/pdf' ||
      mimeType.startsWith('text/')
    );
  };

  const groupedAttachments = () => {
    if (groupBy === 'thread') {
      const grouped: { [key: string]: EmailAttachment[] } = {};
      attachments.forEach(att => {
        const key = `${att.threadId}-${att.threadSubject}`;
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(att);
      });
      return grouped;
    } else if (groupBy === 'type') {
      const grouped: { [key: string]: EmailAttachment[] } = {};
      attachments.forEach(att => {
        const category = getFileTypeCategory(att.attachment.mimeType);
        if (!grouped[category]) grouped[category] = [];
        grouped[category].push(att);
      });
      return grouped;
    } else {
      // Group by date (day)
      const grouped: { [key: string]: EmailAttachment[] } = {};
      attachments.forEach(att => {
        const dateKey = formatDate(att.receivedAt);
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(att);
      });
      return grouped;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-3">
            <Paperclip className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {t('contact.attachments')} - {contact.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {attachments.length} {attachments.length === 1 ? 'attachment' : 'attachments'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Controls */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-600 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Group by:</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value as 'thread' | 'type' | 'date')}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="thread">Thread</option>
              <option value="type">Type</option>
              <option value="date">Date</option>
            </select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12 text-error-600 dark:text-error-400">{error}</div>
          ) : attachments.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No attachments found for this contact.
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedAttachments()).map(([key, groupAttachments]) => (
                <div key={key} className="border-b border-gray-200 dark:border-gray-600 pb-6 last:border-b-0">
                  {groupBy === 'thread' && (
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        {groupAttachments[0].threadSubject}
                      </h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        From: {groupAttachments[0].from} • {groupAttachments.length} attachment(s)
                      </p>
                    </div>
                  )}
                  {groupBy === 'type' && (
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                      {key} ({groupAttachments.length})
                    </h3>
                  )}
                  {groupBy === 'date' && (
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                      {key} ({groupAttachments.length})
                    </h3>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {groupAttachments.map((item, idx) => {
                      const Icon = getFileIcon(item.attachment.mimeType);
                      return (
                        <div
                          key={`${item.threadId}-${item.messageId}-${idx}`}
                          className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex-shrink-0">
                            <Icon className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {item.attachment.filename}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatFileSize(item.attachment.size)}
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500">•</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {item.attachment.mimeType}
                              </span>
                            </div>
                            {groupBy !== 'thread' && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 truncate">
                                {item.threadSubject}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                              <Calendar className="w-3 h-3 inline mr-1" />
                              {formatDateTime(item.receivedAt)}
                            </p>
                          </div>
                          <div className="flex items-center gap-1">
                            {canPreview(item.attachment.mimeType) && (
                              <button
                                onClick={() => handlePreviewAttachment(item)}
                                className="flex-shrink-0 p-2 text-gray-400 hover:text-accent-600 dark:hover:text-accent-400 transition-colors"
                                title="Preview"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                            )}
                            <button
                              onClick={() => downloadAttachment(item)}
                              disabled={downloadingId === item.attachment.attachmentId}
                              className="flex-shrink-0 p-2 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors disabled:opacity-50"
                              title="Download"
                            >
                              {downloadingId === item.attachment.attachmentId ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : (
                                <Download className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {previewAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
            {/* Preview Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-600">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-accent-600 dark:text-accent-400" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {previewAttachment.attachment.filename}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {previewAttachment.threadSubject}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadAttachment(previewAttachment)}
                  disabled={downloadingId === previewAttachment.attachment.attachmentId}
                  className="p-2 text-gray-400 hover:text-purple-600 dark:hover:text-purple-400 transition-colors disabled:opacity-50"
                  title="Download"
                >
                  {downloadingId === previewAttachment.attachment.attachmentId ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Download className="w-5 h-5" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setPreviewAttachment(null);
                    setPreviewData(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Preview Content */}
            <div className="flex-1 overflow-auto p-6 flex items-center justify-center bg-gray-50 dark:bg-gray-900">
              {isLoadingPreview ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600 dark:text-purple-400 mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">Loading preview...</p>
                </div>
              ) : previewData ? (
                <div className="w-full h-full flex items-center justify-center">
                  {previewData.contentType.startsWith('image/') ? (
                    <img
                      src={previewData.dataUrl}
                      alt={previewAttachment.attachment.filename}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : previewData.contentType === 'application/pdf' ? (
                    <iframe
                      src={previewData.dataUrl}
                      className="w-full h-full min-h-[600px] border-0"
                      title={previewAttachment.attachment.filename}
                    />
                  ) : previewData.contentType.startsWith('text/') ? (
                    <iframe
                      src={previewData.dataUrl}
                      className="w-full h-full min-h-[600px] border border-gray-200 dark:border-gray-600 rounded"
                      title={previewAttachment.attachment.filename}
                    />
                  ) : (
                    <div className="text-center py-12">
                      <File className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400">
                        Preview not available for this file type
                      </p>
                      <button
                        onClick={() => downloadAttachment(previewAttachment)}
                        className="mt-4 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                      >
                        Download instead
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  Failed to load preview
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

